// DarkModeToggle — five-state cycle: Light → Midnight (navy) → OLED
// (true black) → Amethyst (purple) → Crimson (red) → Light.
//
// One button, five vibes:
//   ☀️  Sun          — Sanctuary (warm cream, default Holy Words look)
//   🌙  Moon duo     — Midnight Worship (navy depths + gold)
//   ⚫  Moon filled  — Pure Black / OLED (true black + white text)
//   🟣  Moon purple  — Amethyst Night (royal purple + lavender)
//   🟥  Moon red     — Crimson Sanctuary (wine red + warm amber)
//
// We chose a single-button cycle (vs five separate toggles) so the
// mobile toolbar stays tidy. The palette picker next to it still lets
// users jump straight to any of the named themes.
import React from "react";
import { Moon, Sun, Circle } from "@phosphor-icons/react";
import { useTheme } from "../context/ThemeContext";

// Cycle order — light → navy → true black → purple → red → back to
// light. Each click rotates one step in this ring. Themes NOT in the
// ring (royal, sunrise, etc.) stay reachable from the palette picker;
// clicking the toggle while one of those is active jumps into
// "midnight" so the user never gets stuck in a non-cycle theme.
const CYCLE = ["sanctuary", "midnight", "oled", "amethyst", "crimson"];

export default function DarkModeToggle({ className = "" }) {
  const { themeId, setThemeId } = useTheme();
  const idx = CYCLE.indexOf(themeId);
  const next = idx === -1 ? "midnight" : CYCLE[(idx + 1) % CYCLE.length];

  // Icon + tint picked to match the CURRENT state, not the next one,
  // so users can glance at the icon and know what's active without
  // memorising button semantics.
  let Icon = Moon;
  let label = "Dark mode";
  let tint = "bg-cream-200 hover:bg-cream-300 text-ink";
  if (themeId === "midnight") {
    Icon = Sun;
    label = "Pure black mode";
    tint = "bg-amber-400/20 text-amber-300 hover:bg-amber-400/30";
  } else if (themeId === "oled") {
    Icon = Circle;
    label = "Amethyst purple mode";
    tint = "bg-white/15 text-white hover:bg-white/25 ring-1 ring-white/30";
  } else if (themeId === "amethyst") {
    Icon = Circle;
    label = "Crimson red mode";
    tint = "bg-purple-500/25 text-purple-200 hover:bg-purple-500/40 ring-1 ring-purple-300/40";
  } else if (themeId === "crimson") {
    Icon = Circle;
    label = "Light mode";
    tint = "bg-red-500/25 text-amber-300 hover:bg-red-500/40 ring-1 ring-amber-300/40";
  }

  const currentLabel = (
    themeId === "oled" ? "pure black"
    : themeId === "midnight" ? "dark navy"
    : themeId === "amethyst" ? "amethyst purple"
    : themeId === "crimson"  ? "crimson red"
    : "light"
  );

  return (
    <button
      type="button"
      onClick={() => setThemeId(next)}
      data-testid="dark-mode-toggle"
      aria-label={`Currently ${currentLabel} mode. Tap to switch to ${label}.`}
      title={`Tap for ${label}`}
      className={`relative w-9 h-9 rounded-full grid place-items-center transition-colors ${tint} ${className}`}
    >
      <Icon size={15} weight={themeId === "oled" || themeId === "midnight" || themeId === "amethyst" || themeId === "crimson" ? "fill" : "duotone"} />
    </button>
  );
}
